import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'

const DEBOUNCE_DELAY = 300

export interface FilterValues {
  offeringTypes?: ('deal' | 'bundle')[]
  genreIds?: string[]
  networkIds?: string[]
  cpmRange?: { min?: number; max?: number }
  airDates?: { start?: Date; end?: Date }
}

type Section = 'air-dates' | 'cpm-range' | 'genres' | 'networks' | 'offering-type'

export interface FilterContextType {
  // Filter state management
  filters: FilterValues
  debouncedFilters: FilterValues
  handleFilterChange: (filters: Partial<FilterValues>) => void
  updateFilter: <K extends keyof FilterValues>(key: K, value: FilterValues[K]) => void
  clearFilters: () => void
  activeFiltersCount: number

  // Open sections tracking
  openSections: Partial<Record<Section, boolean>>
  toggleSection: (section: Section) => void
  isSectionOpen: (section: Section) => boolean
}

// Default values for the context
const defaultContextValue: FilterContextType = {
  filters: {},
  debouncedFilters: {},
  handleFilterChange: () => {},
  updateFilter: () => {},
  clearFilters: () => {},
  activeFiltersCount: 0,
  openSections: {},
  toggleSection: () => {},
  isSectionOpen: () => false,
}

export const FilterContext = createContext<FilterContextType>(defaultContextValue)

export interface FilterProviderProps {
  children: ReactNode
  initialFilters?: FilterValues
  initialOpenSections?: Partial<Record<Section, boolean>>
}

export const FilterProvider = ({ children, initialFilters = {}, initialOpenSections = {} }: FilterProviderProps) => {
  // Filter state
  const [filters, setFilters] = useState<FilterValues>(initialFilters)
  // Debounced state for API calls
  const [debouncedFilters, setDebouncedFilters] = useState<FilterValues>(initialFilters)

  // Debounce effect (similar to search context)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters)
    }, DEBOUNCE_DELAY)

    return () => clearTimeout(timer)
  }, [filters])

  // Section state
  const [openSections, setOpenSections] = useState<Partial<Record<Section, boolean>>>(initialOpenSections)

  // Update filters
  const handleFilterChange = useCallback((newFilters: Partial<FilterValues>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
    }))
  }, [])

  // Update filter
  const updateFilter = useCallback(<K extends keyof FilterValues>(key: K, value: FilterValues[K]) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }))
  }, [])

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters({})
  }, [])

  // Toggle section open/closed state
  const toggleSection = useCallback((section: Section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }))
  }, [])

  // Check if a section is open
  const isSectionOpen = useCallback(
    (section: Section) => {
      return !!openSections[section]
    },
    [openSections],
  )

  // Count active filters
  const activeFiltersCount = useMemo(() => {
    return Object.values(filters).reduce((count, filterValue) => {
      const isValidArrayFilter = Array.isArray(filterValue) && filterValue.length > 0
      const isValidRangeFilter =
        filterValue &&
        typeof filterValue === 'object' &&
        !Array.isArray(filterValue) &&
        ('min' in filterValue || 'max' in filterValue)
      const isValidDateRangeFilter =
        filterValue &&
        typeof filterValue === 'object' &&
        !Array.isArray(filterValue) &&
        ('start' in filterValue || 'end' in filterValue)

      return isValidArrayFilter || isValidRangeFilter || isValidDateRangeFilter ? count + 1 : count
    }, 0)
  }, [filters])

  const value = useMemo(
    () => ({
      // Filter state
      filters,
      debouncedFilters,
      handleFilterChange,
      updateFilter,
      clearFilters,
      activeFiltersCount,

      // Section state
      openSections,
      toggleSection,
      isSectionOpen,
    }),
    [
      filters,
      debouncedFilters,
      handleFilterChange,
      updateFilter,
      clearFilters,
      activeFiltersCount,
      openSections,
      toggleSection,
      isSectionOpen,
    ],
  )

  return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
}

// Hook to use the filter context
export const useFilter = () => {
  const context = useContext(FilterContext)
  return context
}
